import {
  extractPmr171Frames,
  parseHexBytes,
} from '../../../services/radioSerialProtocol';

export type FactoryParamId =
  | 'iqDmr'
  | 'iqNfm'
  | 'iqOther'
  | 'paBiasLow'
  | 'paBiasHigh'
  | 'rd16'
  | 'fwdPower'
  | 'swr'
  | 'txFreq'
  | 'rxFreq'
  | 'sMeter'
  | 'voltage'
  | 'txIqBalance'
  | 'iqPhase'
  | 'loPhase'
  | 'loAmplitude';

export interface FactoryParamSpec {
  id: FactoryParamId;
  typeCode: number;
  label: string;
  hint: string;
  min: number;
  max: number;
  unit?: string;
  needsBand?: boolean;
  /** 读写 0x55 等前自动发 0x0A 的模式码 */
  preludeMode?: number;
  /** 读写 0x56 前自动发 0x2C：0 = 低功率 / 5W，1 = 高功率 / 30W */
  preludePowerLevel?: 0 | 1;
}

/** 0x1D 频段码 1–13，显示为协议业余频段（MHz） */
export const FACTORY_BAND_LABELS = [
  '1.8',
  '3.5',
  '5',
  '7',
  '10',
  '14',
  '18',
  '21',
  '24',
  '28',
  '50',
  '144',
  '430',
] as const;

export const FACTORY_BANDS: ReadonlyArray<{ id: number; label: string }> = FACTORY_BAND_LABELS.map(
  (label, index) => ({ id: index + 1, label }),
);

export function factoryBandLabel(band: number): string {
  return FACTORY_BAND_LABELS[band - 1] ?? String(band);
}

export const FACTORY_BAND_PARAM_IDS = [
  'iqDmr',
  'iqNfm',
  'iqOther',
  'paBiasLow',
  'paBiasHigh',
  'rd16',
  'fwdPower',
  'swr',
  'txIqBalance',
  'iqPhase',
  'loPhase',
  'loAmplitude',
  'txFreq',
  'rxFreq',
] as const;
export type FactoryBandParamId = (typeof FACTORY_BAND_PARAM_IDS)[number];

export const FACTORY_GLOBAL_PARAM_IDS = [
  'sMeter',
  'voltage',
] as const;
export type FactoryGlobalParamId = (typeof FACTORY_GLOBAL_PARAM_IDS)[number];

export const FACTORY_PARAMS: readonly FactoryParamSpec[] = [
  {
    id: 'iqDmr',
    typeCode: 0x55,
    label: 'IQ·DMR',
    hint: '0–7。读写前自动发 0x0A 选 DMR(9) 与当前频段。',
    min: 0,
    max: 7,
    needsBand: true,
    preludeMode: 9,
  },
  {
    id: 'iqNfm',
    typeCode: 0x55,
    label: 'IQ·NFM',
    hint: '0–7。读写前自动发 0x0A 选 NFM(6) 与当前频段。',
    min: 0,
    max: 7,
    needsBand: true,
    preludeMode: 6,
  },
  {
    id: 'iqOther',
    typeCode: 0x55,
    label: 'IQ·其余',
    hint: '0–250。读写前自动发 0x0A 选 USB(0) 代表其余模式与当前频段。',
    min: 0,
    max: 250,
    needsBand: true,
    preludeMode: 0,
  },
  {
    id: 'paBiasLow',
    typeCode: 0x56,
    label: 'P0·5W',
    hint: '0–255。读写前自动发 0x2C 选低功率（5W）与当前频段。',
    min: 0,
    max: 255,
    needsBand: true,
    preludePowerLevel: 0,
  },
  {
    id: 'paBiasHigh',
    typeCode: 0x56,
    label: 'P0·30W',
    hint: '0–255。读写前自动发 0x2C 选高功率（30W）与当前频段。',
    min: 0,
    max: 255,
    needsBand: true,
    preludePowerLevel: 1,
  },
  {
    id: 'rd16',
    typeCode: 0x57,
    label: 'P2',
    hint: '0–255。按频段分档。',
    min: 0,
    max: 255,
    needsBand: true,
  },
  {
    id: 'fwdPower',
    typeCode: 0x58,
    label: 'PD',
    hint: '0–200。按频段分档。',
    min: 0,
    max: 200,
    needsBand: true,
  },
  {
    id: 'swr',
    typeCode: 0x59,
    label: 'SWR',
    hint: '1–250。按频段分档。',
    min: 1,
    max: 250,
    needsBand: true,
  },
  {
    id: 'txIqBalance',
    typeCode: 0x64,
    label: 'IQ平衡',
    hint: '实际值 -128～+127，int8 补码。0x80 = -128（平衡关闭）。按频段分档。',
    min: -128,
    max: 127,
    needsBand: true,
  },
  {
    id: 'iqPhase',
    typeCode: 0x6f,
    label: '相位',
    hint: '实际值 -128～+127，int8 补码。按频段分档。',
    min: -128,
    max: 127,
    needsBand: true,
  },
  {
    id: 'loPhase',
    typeCode: 0x70,
    label: '本振相位',
    hint: '实际值 -128～+127，int8 补码。按频段分档。',
    min: -128,
    max: 127,
    needsBand: true,
  },
  {
    id: 'loAmplitude',
    typeCode: 0x72,
    label: '本振幅度',
    hint: '挡位 0～3，原值下发。按频段分档。',
    min: 0,
    max: 3,
    needsBand: true,
  },
  {
    id: 'txFreq',
    typeCode: 0x60,
    label: 'TF',
    hint: '实际值 -100～+155（0.1 ppm）。发送值 = 实际 + 100。按频段分档。',
    min: -100,
    max: 155,
    unit: '0.1 ppm',
    needsBand: true,
  },
  {
    id: 'rxFreq',
    typeCode: 0x61,
    label: 'RF',
    hint: '实际值 -100～+155（0.1 ppm）。发送值 = 实际 + 100。按频段分档。',
    min: -100,
    max: 155,
    unit: '0.1 ppm',
    needsBand: true,
  },
  {
    id: 'sMeter',
    typeCode: 0x62,
    label: 'S',
    hint: '实际值 -100～+100。发送值 = 实际 + 100。不分频段。',
    min: -100,
    max: 100,
  },
  {
    id: 'voltage',
    typeCode: 0x63,
    label: 'V',
    hint: '0–200。不分频段。',
    min: 0,
    max: 200,
  },
];

const OFFSET_WIRE_COMMANDS = new Set([0x60, 0x61, 0x62]);
const INT8_WIRE_COMMANDS = new Set([0x64, 0x6f, 0x70]);

/** 0x64 / 0x6F / 0x70：实际值按 int8 补码下发。+128 与 -128 同字节 0x80。 */
export function toInt8Wire(actual: number): number {
  return actual === 128 ? 0x80 : actual & 0xff;
}

/** 0x64 / 0x6F / 0x70：线值按 int8 补码还原为实际值。 */
export function fromInt8Wire(wire: number): number {
  return (wire << 24) >> 24;
}

export function toWireValue(typeCode: number, actual: number): number {
  if (INT8_WIRE_COMMANDS.has(typeCode & 0xff)) {
    return toInt8Wire(actual);
  }
  return OFFSET_WIRE_COMMANDS.has(typeCode) ? actual + 100 : actual;
}

export function fromWireValue(typeCode: number, wire: number): number {
  if (INT8_WIRE_COMMANDS.has(typeCode & 0xff)) {
    return fromInt8Wire(wire);
  }
  return OFFSET_WIRE_COMMANDS.has(typeCode) ? wire - 100 : wire;
}

export function factoryActualRange(spec: { min: number; max: number }): { min: number; max: number } {
  return { min: spec.min, max: spec.max };
}

export function factoryActualInRange(spec: FactoryParamSpec, raw: string): number | null {
  const trimmed = raw.trim();
  if (!trimmed || !/^-?\d+$/.test(trimmed)) return null;
  const actual = Number(trimmed);
  const { min, max } = factoryActualRange(spec);
  if (actual < min || actual > max) return null;
  return actual;
}

export function factoryWriteActualRange(spec: { id: string; min: number; max: number }): { min: number; max: number } {
  return { min: spec.min, max: spec.max };
}

export function factoryWriteActualInRange(spec: { id: string; min: number; max: number }, raw: string): number | null {
  const trimmed = raw.trim();
  if (!trimmed || !/^-?\d+$/.test(trimmed)) return null;
  const actual = Number(trimmed);
  const { min, max } = factoryWriteActualRange(spec);
  if (actual < min || actual > max) return null;
  return actual;
}

export function parseFactoryReply(rxHex: string | null | undefined, typeCode: number): number | null {
  if (!rxHex) return null;
  try {
    const { frames } = extractPmr171Frames(parseHexBytes(rxHex));
    const frame = [...frames]
      .reverse()
      .find(item => (item.command & 0xff) === (typeCode & 0xff) && item.crcValid);
    if (!frame) return null;
    if (typeCode === 0x65 || typeCode === 0x6d) {
      if (frame.data.length < 1) return null;
      return frame.data[0] & 0xff;
    }
    if (frame.data.length < 2) return null;
    const wire = frame.data[1] & 0xff;
    return fromWireValue(typeCode, wire);
  } catch {
    return null;
  }
}

export const FACTORY_BAND_PARAMS = FACTORY_PARAMS.filter(spec => spec.needsBand);
export const FACTORY_GLOBAL_PARAMS = FACTORY_PARAMS.filter(spec => !spec.needsBand);

export function defaultBandParamValues(): Record<FactoryBandParamId, string> {
  return {
    iqDmr: '',
    iqNfm: '',
    iqOther: '',
    paBiasLow: '',
    paBiasHigh: '',
    rd16: '',
    fwdPower: '',
    swr: '',
    txIqBalance: '',
    iqPhase: '',
    loPhase: '',
    loAmplitude: '',
    txFreq: '',
    rxFreq: '',
  };
}

export function defaultGlobalParamValues(): Record<FactoryGlobalParamId, string> {
  return {
    sMeter: '',
    voltage: '',
  };
}

export function defaultFactoryValues(): Record<FactoryParamId, string> {
  return {
    ...defaultBandParamValues(),
    ...defaultGlobalParamValues(),
  };
}

export interface FactoryBandRowState {
  values: Record<FactoryBandParamId, string>;
}

export function defaultFactoryBandRow(): FactoryBandRowState {
  return {
    values: defaultBandParamValues(),
  };
}

export function defaultFactoryBandRows(): Record<number, FactoryBandRowState> {
  return Object.fromEntries(
    FACTORY_BANDS.map(item => [item.id, defaultFactoryBandRow()]),
  ) as Record<number, FactoryBandRowState>;
}

export function factoryCommandLabel(typeCode: number): string {
  return `0x${typeCode.toString(16).toUpperCase().padStart(2, '0')}`;
}

/** MQTT FACT 单页：IQ / P0 各一条，范围随当前模式与高低档变化。 */
export type FactorySheetFieldId =
  | 'iq'
  | 'p0'
  | 'rd16'
  | 'fwdPower'
  | 'swr'
  | 'txFreq'
  | 'rxFreq'
  | 'sMeter'
  | 'voltage'
  | 'txIqBalance'
  | 'iqPhase'
  | 'loPhase'
  | 'loAmplitude';

export type FactorySheetSpec = Omit<FactoryParamSpec, 'id'> & { id: FactorySheetFieldId };

export function emptyFactorySheetValues(): Record<FactorySheetFieldId, string> {
  return {
    iq: '',
    p0: '',
    rd16: '',
    fwdPower: '',
    swr: '',
    txFreq: '',
    rxFreq: '',
    sMeter: '',
    voltage: '',
    txIqBalance: '',
    iqPhase: '',
    loPhase: '',
    loAmplitude: '',
  };
}

export function isIqNarrowMode(modeCode: number): boolean {
  return modeCode === 6 || modeCode === 9;
}

export function factorySheetSpecs(modeCode: number, highLow: 0 | 1): FactorySheetSpec[] {
  const iqNarrow = isIqNarrowMode(modeCode);
  const shared = FACTORY_PARAMS.filter(
    (spec) =>
      spec.id === 'rd16' ||
      spec.id === 'fwdPower' ||
      spec.id === 'swr' ||
      spec.id === 'txFreq' ||
      spec.id === 'rxFreq' ||
      spec.id === 'sMeter' ||
      spec.id === 'voltage' ||
      spec.id === 'txIqBalance' ||
      spec.id === 'iqPhase' ||
      spec.id === 'loPhase' ||
      spec.id === 'loAmplitude',
  ) as FactorySheetSpec[];
  return [
    {
      id: 'iq',
      typeCode: 0x55,
      label: 'IQ',
      hint: iqNarrow ? '0–7。先选模式再读写。' : '0–250。先选模式再读写。',
      min: 0,
      max: iqNarrow ? 7 : 250,
      needsBand: true,
      preludeMode: modeCode,
    },
    {
      id: 'p0',
      typeCode: 0x56,
      label: 'P0',
      hint: highLow === 1 ? '0–255。先发 0x2C 选高功率（30W）再读写。' : '0–255。先发 0x2C 选低功率（5W）再读写。',
      min: 0,
      max: 255,
      needsBand: true,
      preludePowerLevel: highLow,
    },
    ...shared,
  ];
}

export interface FactorySheetState {
  engineeringMode: boolean;
  band: number;
  modeCode: number;
  highLow: 0 | 1;
  values: Record<FactorySheetFieldId, string>;
}

export function defaultFactorySheetState(): FactorySheetState {
  return {
    engineeringMode: false,
    band: 1,
    modeCode: 0,
    highLow: 0,
    values: emptyFactorySheetValues(),
  };
}

export interface FactoryNetworkParams {
  localIp: string;
  netmask: string;
  gateway: string;
  remoteIp: string;
  tcpPort: string;
  udpPort: string;
}

export function defaultFactoryNetworkParams(): FactoryNetworkParams {
  return {
    localIp: '',
    netmask: '',
    gateway: '',
    remoteIp: '',
    tcpPort: '',
    udpPort: '',
  };
}

export function parseIpv4(raw: string): number[] | null {
  const parts = raw.trim().split('.');
  if (parts.length !== 4) return null;
  const octets = parts.map(part => {
    if (!/^\d+$/.test(part)) return Number.NaN;
    return Number(part);
  });
  if (octets.some(octet => !Number.isInteger(octet) || octet < 0 || octet > 255)) return null;
  return octets;
}

export function parsePort(raw: string): number | null {
  if (!/^\d+$/.test(raw.trim())) return null;
  const port = Number(raw.trim());
  if (port < 1 || port > 65535) return null;
  return port;
}

function formatIpv4(bytes: ArrayLike<number>, offset: number): string {
  return [
    bytes[offset] & 0xff,
    bytes[offset + 1] & 0xff,
    bytes[offset + 2] & 0xff,
    bytes[offset + 3] & 0xff,
  ].join('.');
}

function readPortLe(bytes: ArrayLike<number>, offset: number): number {
  return (bytes[offset] & 0xff) | ((bytes[offset + 1] & 0xff) << 8);
}

export function parseNetworkReply(rxHex: string | null | undefined): FactoryNetworkParams | null {
  if (!rxHex) return null;
  try {
    const { frames } = extractPmr171Frames(parseHexBytes(rxHex));
    const frame = [...frames]
      .reverse()
      .find(item => (item.command & 0xff) === 0x6e && item.crcValid);
    if (!frame || frame.data.length < 21) return null;
    return {
      localIp: formatIpv4(frame.data, 1),
      netmask: formatIpv4(frame.data, 5),
      gateway: formatIpv4(frame.data, 9),
      remoteIp: formatIpv4(frame.data, 13),
      tcpPort: String(readPortLe(frame.data, 17)),
      udpPort: String(readPortLe(frame.data, 19)),
    };
  } catch {
    return null;
  }
}
